<template>
  <div v-if="dataLoaded">

    <!-- Row top buttons -->
    <div class="row q-mb-lg">
      <div class="col-6" style="justify-content flex-start">
        <q-btn
          class="capitalize ctrl-button"
          color="grey-5"
          :label="`${$store.state.stringsStore.strings.action.toOverview}`"
          @click="gotoRoute(`/${moduleContext}`)"
          icon="keyboard_backspace"
        />
      </div>
      <div class="col-6">
        <q-btn  style="float: right"
          class="capitalize ctrl-button right"
          :color="containsErrors ? 'grey-5' : 'ctrl-turquoise'"
          :label="`${$store.state.stringsStore.strings.action.save}`"
          :disable="containsErrors"
          @click="saveChanges('PUT')"
          icon-right="save"
        />
      </div>
    </div>

    <!-- Columns -->
    <div class="row gutter-sm q-mb-lg">

      <!-- Left column -->
      <div class="col-md-6 col-sm-12 col-xs-12 q-pa-sm">

        <!-- User block -->
        <div class="row q-mb-sm">
          <div class="full-width">
            <p class="card-title">{{ $store.state.stringsStore.strings.module.users.single }}</p>
            <div class="card-block scroll">
              <!-- Field: first name -->
              <q-field class="q-mb-md"
                :error="validations.firstname.warning"
                :error-label="validations.firstname.message">
                <q-input type="text" v-model="data.name.firstname"
                  @blur="validateField(data.name.firstname, 'firstname')"
                  :float-label="`${$store.state.stringsStore.strings.firstname} *`">
                </q-input>
              </q-field>

              <!-- Field: last name -->
              <q-field class="q-mb-md"
                :error="validations.surname.warning"
                :error-label="validations.surname.message">
                <q-input type="text" v-model="data.name.surname"
                  @blur="validateField(data.name.surname, 'surname')"
                  :float-label="`${$store.state.stringsStore.strings.surname} *`">
                </q-input>
              </q-field>

              <!-- Field: email -->
              <q-field class="q-mb-md"
                :error="validations.email.warning"
                :error-label="validations.email.message">
                <q-input type="text" v-model="data.email"
                 @blur="validateField(data.email, 'email')"
                  :float-label="`${$store.state.stringsStore.strings.email} *`">
                </q-input>
              </q-field>

              <!-- Field: super user -->
              <q-field class="q-mb-md"
                v-if="editSuAllowed">
                <q-toggle v-model="data.su" color="secondary">
                  <span style="margin-left: 1em;">
                    {{ $store.state.stringsStore.strings.module.users.edit.su }}
                  </span>
                </q-toggle>
              </q-field>
            </div>
          </div>
        </div>

        <!-- Websites block -->
        <div class="row q-mb-sm">
          <div class="full-width">
            <p class="card-title">{{ $store.state.stringsStore.strings.website }}</p>
            <div class="card-block scroll">
              <div class="list item-inset-delimiter">
                <!-- Each available website -->
                <div class="item q-pt-md q-pb-md q-pl-sm q-pr-sm delimiter"
                  v-for="website in $store.state.meStore.me.websites"
                  :key="website.name">
                  <div class="row">
                    <div class="col-6">
                      {{ website.name }}
                    </div>
                    <div class="col-6">
                      <q-toggle v-model="data.websites"
                        class="float-right"
                        :val="website.id" color="secondary"/>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>

      <!-- Right column -->
      <div class="col-md-6 col-sm-12 col-xs-12">

        <!-- Search users block -->
        <div class="row q-mb-lg">
          <div class="full-width">
            <p class="card-title">{{ $store.state.stringsStore.strings.module.name.usergroups }}</p>
            <!-- Searchfield -->
            <div class="q-mb-md" style="flex-grow: 1">
              <q-input
                color="grey-5"
                v-model="searchTerm"
                @input="applyFilters"
                :placeholder="$store.state.stringsStore
                .strings.module.users.invite.list.filter.placeholder"
              />
            </div>
            <!-- Data table -->
            <div class="q-table" id="myTable">
              <vuetable
                ref="vuetable"
                class="table-block q-mb-md"
                :class="{ 'is-loading': isLoading }"
                :table-height="tableHeight"
                :api-url="'usergroup'"
                pagination-path
                :api-mode="true"
                :noDataTemplate="noDataString('usergroups')"
                :css="css.table"
                :http-fetch="fetchData"
                :fields="columns"
                :row-class="selectable ? applyRowClass : null"
                @vuetable:loading="onLoading"
                @vuetable:load-success="onSuccess"
                @vuetable:load-error="onError"
                @vuetable:pagination-data="onPaginationData">
                <!-- Custom fields -->
                <div slot="name" slot-scope="props">
                  <div v-html="highlight(props.rowData.name, filters.name)"></div>
                </div>
                <div slot="member" slot-scope="props">
                  <q-toggle v-model="data.usergroups" :val="props.rowData.id" color="secondary"/>
                </div>
              </vuetable>
              <!-- Pagination -->
              <div class="row pagination-bottom" id="myPagination">
                <!-- Per page select -->
                <div class="col">
                    <q-select
                      class="btn-compact select-small float-left"
                      hide-underline
                      color="grey"
                      v-model="perPage"
                      :options="perPageOptions"
                    />
                </div>
                <div class="col">
                  <vuetable-pagination
                    class="float-right"
                    :on-each-side="1"
                    :css="css.pagination"
                    @vuetable-pagination:change-page="onChangePage"
                    ref="pagination">
                  </vuetable-pagination>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vuetable from 'vuetable-2';
import _get from 'lodash.get';
import VuetablePagination from 'vuetable-2/src/components/VuetablePagination.vue';

// Helpers
import getString from '@/helpers/GetStrings';

// Mixins
import tableMixin from '@/mixins/tables/tableMixin';
import pagination from '@/mixins/tables/pagination';
import searchHighlight from '@/mixins/tables/searchHighlight';
import fetchData from '@/mixins/tables/fetchData';
import fetchDetail from '@/mixins/detailViews/fetchDetail';
import saveChanges from '@/mixins/detailViews/saveChanges';
import common from '../common';

export default {
  mixins: [
    tableMixin,
    pagination,
    searchHighlight,
    fetchData,
    fetchDetail,
    saveChanges,
    common,
  ],
  components: {
    Vuetable,
    VuetablePagination,
  },
  name: 'UsersDetail',
  data() {
    return {
      responseData: {
        multiple: 'usergroups',
      },
      tableHeight: 'calc(100vh - 360px)',
      validations: {
        firstname: {
          warning: false,
          message: '',
        },
        surname: {
          warning: false,
          message: '',
        },
        email: {
          warning: false,
          message: '',
        },
      },
      customSort: {
        field: 'user',
      },
      containsErrors: false,
      selectable: false,
      dataLoaded: false,
      data: null,
      columns: [
        {
          name: '__slot:name',
          title: getString('name'),
          label: 'name',
          sortField: 'name',
          titleClass: 'text-left sortable vcol-l',
          dataClass: 'text-left vcol-l',
        },
        {
          name: '__slot:member',
          title: getString('member'),
          label: 'member',
          sortField: 'member',
          titleClass: 'text-left sortable vcol-xs',
          dataClass: 'text-left vcol-xs',
        },
      ],
      tableContext: 'usergroups',
      tableEndpoint: 'usergroup',
      moduleContext: 'users',
      moduleEndpoint: 'user',
      isLoading: false,
      filters: {
        name: '',
      },
    };
  },
  methods: {
    validateField(value, field) {
      const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
      const nameRegex = /^[a-zA-Z]([\w -]*[a-zA-Z])?$/;
      if (value === '') {
        this.validations[field].warning = true;
        this.validation[field].message = this.getValidationString(field, 'required');
      } else if (!nameRegex.test(value) && field !== 'email') {
        this.validations[field].warning = true;
        this.validations[field].message = this.getValidationString(field, 'pattern.default');
      } else if (!emailRegex.test(value) && field === 'email') {
        this.validations[field].warning = true;
        this.validations[field].message = this.getValidationString(field, 'pattern.email');
      } else {
        this.validations[field].warning = false;
        this.validations[field].message = '';
      }
      this.checkValidationErrors();
      return this.validations[field].warning;
    },
    getValidationString(field, error) {
      let message = _get(this.$store.state.stringsStore.strings.form.error, error);
      if (message.match('%possesivePronoun%')) {
        message = message.replace('%possesivePronoun%',
          `${this.$store.state.stringsStore.strings.possesivePronoun.other} `);
      }
      if (message.match('%input%')) {
        message = message
          .replace('%input%', field);
      }
      return message;
    },
    gotoRoute(path) {
      this.$router.push(path);
    },
    requestParams() {
      const usergroups = [];
      const websites = [];

      // Transform super user field to integer value.
      const su = this.data.su ? 1 : 0;

      // Push a new query string for each usergroup present in the data.usergroups array.
      this.data.usergroups.forEach((id) => {
        usergroups.push(`usergroups[]=${id}`);
      });

      // Push a new query string for each website present in the data.websites array.
      this.data.websites.forEach((id) => {
        websites.push(`websites[]=${id}`);
      });

      // Join each field for the user PUT request. This string is returned by the function.
      // We send every field, regardless of whether it has changed or not.
      return [
        `firstname=${this.data.name.firstname}`,
        `surname=${this.data.name.surname}`,
        `email=${this.data.email}`,
        this.editSuAllowed ? `su=${su}` : null, // Only send this param when the user is allowed.
        usergroups.length ? usergroups.join('&') : null,
        websites.join('&'),
      ].filter(val => val !== null).join('&');
    },
    checkValidationErrors() {
      let errors = false;
      Object.values(this.validations).forEach((value) => {
        if (value.warning) {
          errors = true;
        }
      });
      this.containsErrors = errors;
    },
  },
  computed: {
    fullName() {
      return [this.data.name.firstname, this.data.name.surname].filter(val => val !== null).join(' ');
    },
    editSuAllowed() {
      return this.$store.state.meStore.me.su
      && this.$store.state.meStore.me.id !== this.data.id;
    },
  },
  filters: {
    checkEmpty(value) {
      return value === null || value === '' ? '-' : value;
    },
    capitalize(value) {
      if (!value) return '';
      value = value.toString();
      return value.charAt(0).toUpperCase() + value.slice(1);
    },
  },
};
</script>

<style lang="stylus" scoped>
@import '~variables'
</style>
